import type { MouseEvent, ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { cx } from '../components/ui';

/**
 * Everything links to its details, so people can hop between a lead, its quotation, the customer and
 * back without losing their place. Leads and customers open in a side panel on top of the current page
 * (`?lead=…` / `?customer=…` in the address, so Back closes it and links can be shared).
 */

export type RecordKind = 'lead' | 'customer';

/** The current address with one record opened (or closed with null). */
export function withRecord(search: string, kind: RecordKind, id: string | null): string {
  const p = new URLSearchParams(search);
  for (const k of ['lead', 'customer'] as const) p.delete(k); // one panel at a time
  if (id) p.set(kind, id);
  const s = p.toString();
  return s ? `?${s}` : '';
}

const linkClass = 'font-medium text-brand-700 hover:underline dark:text-brand-400';
// Links sit inside clickable rows: a click on the link shouldn't also trigger the row.
const stop = (e: MouseEvent) => e.stopPropagation();

function RecordLink({ kind, id, children, className, plain }: { kind: RecordKind; id: string; children: ReactNode; className?: string; plain?: boolean }) {
  const location = useLocation();
  return (
    <Link to={{ pathname: location.pathname, search: withRecord(location.search, kind, id) }} onClick={stop} className={cx(!plain && linkClass, className)}>
      {children}
    </Link>
  );
}

/** "L-12 Ramesh Kumar" → opens the lead. */
export function LeadLink({ id, number, name, children, className, plain }: { id: string; number?: number | null; name?: string | null; children?: ReactNode; className?: string; plain?: boolean }) {
  return <RecordLink kind="lead" id={id} className={className} plain={plain}>{children ?? `${number ? `L-${number} ` : ''}${name ?? 'Lead'}`}</RecordLink>;
}

export function CustomerLink({ id, children, className, plain }: { id: string; children: ReactNode; className?: string; plain?: boolean }) {
  return <RecordLink kind="customer" id={id} className={className} plain={plain}>{children}</RecordLink>;
}

/** Quotation / invoice number → its page. */
export function DocLink({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  return <Link to={`/sales/documents/${id}`} onClick={stop} className={cx(linkClass, className)}>{children}</Link>;
}

/** A salesperson's name → their leads. */
export function OwnerLink({ id, children, className }: { id: string; children: ReactNode; className?: string }) {
  return <Link to={`/leads?owner=${id}`} onClick={stop} className={cx('hover:underline', className)}>{children}</Link>;
}
